import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Group,
  Modal,
  ScrollAreaAutosize,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useUpdateFollowUp } from "@hooks/mutations/followUp/useUpdateFollowUp";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { FollowUp } from "@customTypes/followUp";


interface ModalProps {
  opened: boolean;
  close: () => void;
  followUp: Partial<FollowUp> | null;
}

export const CloseFollowUpModal = ({
  opened,
  close,
  followUp: initialFollowUp,
}: ModalProps) => {
  const { t } = useTranslation();

  const [reason, setReason] = useState("");
  const [followUp, setFollowUp] = useState<Partial<FollowUp>>({});
  const [patientId, setPatientId] = useState("");

  const { mutate: closeFollowUp, isLoading: isCloseFollowUpLoading } =
    useUpdateFollowUp();
  const handleErrors = useQueryErrorHandling();

  const handleSubmit = () => {
    if (!reason || followUp?.billStatus !== "paid") return;
    const id = showLoadingNotification({});

    closeFollowUp(
      {
        intent: "close",
        followUpId: followUp.id,
        patientId,
        reason,
      },
      {
        onSuccess: () => {
          showSuccessNotification({
            id,
            title: "success",
            message: "update-follow-up-success",
          });
          close();
        },
        onError: (res: any) => {
          handleErrors(res, "mutation", id);
        },
      }
    );
  };

  useEffect(() => {
    if (initialFollowUp?.id) {
      setFollowUp(initialFollowUp);
      setReason(followUp?.reason ?? "");
      setPatientId(followUp?.patientId ?? "");
    }
  }, [initialFollowUp]);

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("close-follow-up")}</Text>}
      opened={opened}
      onClose={close}
      styles={{ body: { minHeight: "auto" } }}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Stack>
          <Textarea
            withAsterisk
            label={t("reason")}
            rows={5}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </Stack>

        <Group justify="end" mt={"xl"} pb={"0"}>
          <Button
            disabled={!reason}
            onClick={handleSubmit}
            loading={isCloseFollowUpLoading}>
            {t("close-follow-up")}
          </Button>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
